import type { TFunction } from 'i18next'
import type { SessionParticipant, SessionResultsData } from '~/gql/sessions/sessions'
import type { DetailSession } from '~/pages/backoffice/admin/sessions/detail/detail-session'
import { CertificateCondition, RoleEnum, SessionProgressionMode, SessionStatus, UserProgress } from '@zeus/index'
import { demoPath } from '~/pages/help/demo/demo-path'

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Une session de démonstration pour la visite de la fiche session (#243) : le
 * parcours de démonstration de l'apprenant (#105), vu du back-office, avec
 * trois participants — un de chaque état — pour que chaque bloc ait de quoi
 * être montré.
 */
export function demoSession(t: TFunction, now = new Date()): { session: DetailSession, results: SessionResultsData } {
  const path = demoPath(t, now)
  const modules = (path.modules ?? []).map(step => ({
    id: step.id,
    sectionId: step.sectionId ?? null,
    unlockAt: step.unlockAt ? new Date(String(step.unlockAt)) : null,
    prerequisiteModuleId: step.prerequisiteModuleId ?? null,
    prerequisiteMinScore: step.prerequisiteMinScore ?? null,
    module: { id: step.module?.id ?? step.id, title: step.module?.title ?? '' },
  }))

  const participant = (userId: string, firstName: string, lastName: string, state: UserProgress, completedModules: number, score: number | null, minutes: number, daysAgo: number | null): SessionParticipant => ({
    userId,
    firstName,
    lastName,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
    groups: [t('helpDemo.session.group')],
    state,
    completedModules,
    totalModules: modules.length,
    averageScorePercent: score,
    timeSpentSeconds: minutes * 60,
    lastActivityAt: daysAgo == null ? null : new Date(now.getTime() - daysAgo * DAY_MS),
  })

  return {
    session: {
      id: 'demo-session',
      name: path.name,
      status: SessionStatus.ACTIVE,
      createdAt: new Date(now.getTime() - 30 * DAY_MS),
      startDate: new Date(now.getTime() - 7 * DAY_MS),
      endDate: new Date(now.getTime() + 60 * DAY_MS),
      tags: [{ id: 'demo-tag', name: t('helpDemo.session.tag') }],
      trainers: [{ id: 'demo-trainer', firstName: 'Claire', lastName: 'Dubois', email: 'claire.dubois@example.com', role: RoleEnum.Formateur }],
      progressionMode: SessionProgressionMode.FREE,
      certificateCondition: CertificateCondition.SCORE,
      certificateMinScore: 0.8,
      certificateTemplateId: null,
      sections: (path.sections ?? []).map(section => ({ id: section.id, title: section.title, position: section.position })),
      modules,
    },
    results: {
      stats: {
        total: 12,
        learners: 3,
        completed: 6,
        inProgress: 1,
        notStarted: 3,
        completionUnknown: 0,
        neverOpened: 3,
        notYetUnlocked: 2,
        passed: 5,
        failed: 1,
        averageScorePercent: 81.5,
        scoredCount: 6,
        averageTimeSeconds: 960,
        timedCount: 7,
        totalTimeSeconds: 6720,
      },
      participants: [
        participant('demo-1', 'Camille', 'Martin', UserProgress.COMPLETED, 4, 88, 75, 1),
        participant('demo-2', 'Inès', 'Bernard', UserProgress.IN_PROGRESS, 2, 72, 37, 3),
        participant('demo-3', 'Hugo', 'Petit', UserProgress.NOT_STARTED, 0, null, 0, null),
      ],
      modules: modules.map((step, i) => ({ moduleId: step.module.id, completedCount: [2, 2, 1, 1][i] ?? 0 })),
    },
  }
}
